import type { managedWarehouseFr } from './entrepots-geres.fr';

type DeepStrings<T> = { [K in keyof T]: T[K] extends string ? string : DeepStrings<T[K]> };

/** Same keys as `entrepots-geres.fr.ts` (ADR 0100: managed warehouses). */
export const managedWarehouseEn: DeepStrings<typeof managedWarehouseFr> = {
  fields: {
    hosting: 'Hosting',
    hostingHelp:
      'Existing database: graal connects with the account you give. Managed by graal: graal deploys the database on an infrastructure of your organization and keeps its accounts.',
    infrastructure: 'Infrastructure',
    infrastructureHelp:
      'Only trusted Kubernetes infrastructures private to your organization, with a namespace of their own, host a managed database.',
    noInfrastructure: 'No infrastructure can host a managed database.',
    instanceType: 'Instance type',
    instanceTypeHelp: 'CPU and memory of the database pod, among the types allowed on {{name}}.',
    storageSize: 'Volume size',
    storageSizeHelp: 'Kubernetes quantity (10Gi, 500Mi, 1Ti). Empty: 10Gi.',
    storageClass: 'Storage class',
    storageClassHelp:
      'Class of the persistent volume: Ceph storage (RBD, csi-rbd-sc on the platform), never a local disk of the node (local-path, hostPath).',
  },
  hostings: {
    external: 'Existing database (connection)',
    managed: 'Managed by graal',
  },
  infrastructureOption: '{{name}} — namespace {{namespace}}',
  refused: 'Not offered: {{list}}.',
  refusedItem: '{{name}} ({{reason}})',
  refusals: {
    notKubernetes: 'not a Kubernetes cluster',
    untrusted: 'untrusted: cluster controlled by the organization, no secret is sent there',
    shared: 'shared between organizations',
    noNamespace: 'no namespace of its own',
  },
  errors: {
    storageSize: 'A Kubernetes size: a number followed by Mi, Gi or Ti (10Gi).',
    storageClass: 'A storage class name: lowercase letters, digits, “-” and “.”.',
    database: 'Letters, digits and “_”, 63 characters at most.',
    workloadUnsupported:
      'This infrastructure cannot host a managed database (it must be trusted, private to your organization and have a namespace of its own, outside the run namespace). Server reason: {{detail}}',
    workloadUnsupportedField: 'Infrastructure refused by the server for a managed database.',
    infrastructureNotFound: 'Infrastructure not found: it may have been deleted.',
    missing: 'Missing required parameter: {{parameter}}.',
    missingField: 'Required for a managed database.',
    invalid: 'Value refused by the server: {{detail}}',
    invalidField: 'Value refused by the server.',
  },
  connection: 'Address',
  connectionHelp:
    'Set by graal: the Service of the database in the infrastructure namespace. Reachable from the organization’s runs and from graal, not from the Internet.',
  storageSize: 'Volume',
  storageClass: 'Storage class',
  database: 'Database',
  power: {
    start: 'Start',
    stop: 'Stop',
    starting: 'Start of “{{name}}” requested.',
    stopping: 'Stop of “{{name}}” requested: the volume is kept.',
    startFailed: 'The database could not be started.',
    stopFailed: 'The database could not be stopped.',
  },
  delete: {
    action: 'Delete warehouse',
    title: 'Delete warehouse “{{name}}”?',
    description:
      'The database is removed from the cluster (StatefulSet, Service, NetworkPolicy, Secret). By default the volume is kept: its data stays in the infrastructure namespace.',
    purge: 'Also delete the volume (irreversible)',
    purgeHelp: 'The database data is erased for good. graal makes no backup.',
    confirmName: 'To confirm, type the warehouse name: {{name}}',
    confirm: 'Delete',
    confirmPurge: 'Delete with the volume',
    deleted: 'Warehouse “{{name}}” deleted; volume kept.',
    purged: 'Warehouse “{{name}}” and its volume deleted.',
  },
};
